import { type JSX, lazy, Suspense, useEffect } from 'react';
import { Navigate, Outlet, Route, useLocation } from 'react-router';
import {
  enable1MindLinkSignup,
  enableAiConfigOnboarding,
  enableAwsMarketplaceSignup,
  enableDomainMatching,
} from '@gonfalon/dogfood-flags/dogfood-flags';
import { NoisyErrorBoundary } from '@gonfalon/error-boundaries/NoisyErrorBoundary';
import { msg } from '@gonfalon/messages/msg';
import { toLogin } from '@gonfalon/navigator/navigators';
import { useMatchingUnauthenticatedPathPattern } from '@gonfalon/router/useMatchingPathPattern';
import { setRouteContext } from '@gonfalon/telemetry/setRouteContext';
import { ProgressBar, SnackbarRegion } from '@launchpad-ui/components';

import { updateLocation } from '#actions/router';
import { LoginContainer } from '#components/loginAndSignUp/LoginContainer';
import { SignUpRouteEntry } from '#components/signUp/SignUpRouteEntry';
import { useDispatch } from '#hooks/useDispatch';

import { unauthenticatedRouteOwnership } from '#routes/unauthenticatedRouteOwnership';

function LoadingFallback() {
  return <ProgressBar aria-label={msg('login.status.loading')} isIndeterminate />;
}

const SignUpAgentControlRouteEntry = lazy(async () =>
  import(/* webpackChunkName: "SignUpAgentControlRouteEntry" */ '#components/signUp/SignUpAgentControlRouteEntry').then(
    (module) => ({
      default: module.SignUpAgentControlRouteEntry,
    }),
  ),
);

const SignUpAwsRouteEntry = lazy(async () =>
  import(/* webpackChunkName: "SignUpAwsRouteEntry" */ '#components/signUp/SignUpAwsRouteEntry').then((module) => ({
    default: module.SignUpAwsRouteEntry,
  })),
);

const AcceptInvitation = lazy(async () =>
  import(/* webpackChunkName: "AcceptInvitation" */ '#components/loginAndSignUp/AcceptInvitation').then((module) => ({
    default: module.AcceptInvitation,
  })),
);

const FacilitatedTrialSignupContainer = lazy(async () =>
  import(
    /* webpackChunkName: "FacilitatedTrialSignupContainer" */ '#components/loginAndSignUp/FacilitatedTrialSignupContainer'
  ).then((module) => ({
    default: module.Component,
  })),
);

const LazyTrialLimitContainer = lazy(async () =>
  import(/* webpackChunkName: "LazyTrialLimitContainer" */ '#components/loginAndSignUp/TrialLimitContainer').then(
    (module) => ({
      default: module.TrialLimitContainer,
    }),
  ),
);

const ChooseOrganizationPasswordFormContainer = lazy(async () =>
  import(
    /* webpackChunkName: "ChooseOrganizationPasswordFormContainer" */ '#components/loginAndSignUp/ChooseOrganizationPasswordFormContainer'
  ).then((module) => ({
    default: module.ChooseOrganizationPasswordFormContainer,
  })),
);

const EnrollMFAContainer = lazy(async () =>
  import(/* webpackChunkName: "EnrollMFAContainer" */ '#components/loginAndSignUp/EnrollMFAContainer').then(
    (module) => ({
      default: module.EnrollMFAContainer,
    }),
  ),
);

const ChooseOrganizationContainer = lazy(async () =>
  import(
    /* webpackChunkName: "ChooseOrganizationContainer" */ '#components/loginAndSignUp/ChooseOrganizationContainer'
  ).then((module) => ({
    default: module.ChooseOrganizationContainer,
  })),
);
const CreateOrJoinContainer = lazy(async () =>
  import(/* webpackChunkName: "CreateOrJoinContainer" */ '#components/loginAndSignUp/CreateOrJoinContainer').then(
    (module) => ({
      default: module.CreateOrJoinContainer,
    }),
  ),
);

const CreateOrJoinSwitcher = lazy(async () =>
  import(/* webpackChunkName: "CreateOrJoinSwitcher" */ '#components/loginAndSignUp/CreateOrJoinSwitcher').then(
    (module) => ({
      default: module.CreateOrJoinSwitcher,
    }),
  ),
);

const ForgotPasswordContainer = lazy(async () =>
  import(/* webpackChunkName: "ForgotPasswordContainer" */ '#components/loginAndSignUp/ForgotPasswordContainer').then(
    (module) => ({
      default: module.ForgotPasswordContainer,
    }),
  ),
);
const ResetPasswordContainer = lazy(async () =>
  import(/* webpackChunkName: "PasswordContainer" */ '#components/loginAndSignUp/ResetPasswordContainer').then(
    (module) => ({
      default: module.ResetPasswordContainer,
    }),
  ),
);

const OAuthContainer = lazy(async () =>
  import(/* webpackChunkName: "OAuthContainer" */ '#components/loginAndSignUp/OAuthContainer').then((module) => ({
    default: module.OAuthContainer,
  })),
);

const TeamUsesSsoContainer = lazy(async () =>
  import(/* webpackChunkName: "TeamUsesSsoContainer" */ '#components/loginAndSignUp/TeamUsesSsoContainer').then(
    (module) => ({
      default: module.TeamUsesSsoContainer,
    }),
  ),
);

const JoinOrg = lazy(async () =>
  import(/* webpackChunkName: "JoinOrg" */ '#components/loginAndSignUp/JoinOrgContainer').then((module) => ({
    default: module.JoinOrgContainer,
  })),
);

const TrustThisDeviceContainer = lazy(async () =>
  import(/* webpackChunkName: "TrustThisDeviceContainer" */ '#components/loginAndSignUp/TrustThisDeviceContainer').then(
    (module) => ({
      default: module.TrustThisDeviceContainer,
    }),
  ),
);

const UnauthenticatedEmailVerificationContainer = lazy(async () =>
  import(
    /* webpackChunkName: "UnauthenticatedEmailVerificationContainer" */ '#components/loginAndSignUp/UnauthenticatedEmailVerification/UnauthenticatedEmailVerificationContainer'
  ).then((module) => ({
    default: module.UnauthenticatedEmailVerificationContainer,
  })),
);

const Welcome = lazy(async () =>
  import(/* webpackChunkName: "Welcome" */ '#components/loginAndSignUp/welcome/welcome').then((module) => ({
    default: module.Welcome,
  })),
);

const PendingInvite = lazy(async () =>
  import(/* webpackChunkName: "PendingInvite" */ '#components/loginAndSignUp/pendingInvite/pendingInvite').then(
    (module) => ({
      default: module.PendingInvite,
    }),
  ),
);

const isDomainMatchingEnabled = enableDomainMatching();

function UnauthenticatedApp(props: { routes: JSX.Element }) {
  const matchingPathPattern = useMatchingUnauthenticatedPathPattern(props.routes);
  const dispatch = useDispatch();
  const location = useLocation();

  useEffect(() => {
    dispatch(updateLocation(location));
  }, [dispatch, location]);

  useEffect(() => {
    if (matchingPathPattern) {
      const owner = unauthenticatedRouteOwnership[matchingPathPattern];
      setRouteContext(matchingPathPattern, owner);
    }
  }, [matchingPathPattern]);

  return (
    <>
      <NoisyErrorBoundary severity="critical">
        <Suspense fallback={<LoadingFallback />}>
          <Outlet />
        </Suspense>

        <SnackbarRegion />
      </NoisyErrorBoundary>
    </>
  );
}

export function unauthenticatedRoutes() {
  const routes = (
    <>
      <Route path="/choose-organization/:email" element={<ChooseOrganizationContainer />} />

      <Route
        path="/choose-organization/:email/:selectedOrganization/enter-password"
        element={<ChooseOrganizationPasswordFormContainer />}
      />
      <Route path="/choose-organization/:selectedOrganization/forgot" element={<ForgotPasswordContainer />} />

      <Route path="/choose-organization/verify/:token" element={<TrustThisDeviceContainer />} />

      <Route path="/join-your-organization/:token" element={<JoinOrg />} />
      <Route path="/join-your-organization" element={<JoinOrg />} />
      <Route path="/welcome/:token" element={<Welcome />} />
      <Route path="/welcome" element={<Welcome />} />
      <Route path="/pending-invite/:token" element={<PendingInvite />} />
      <Route path="/pending-invite" element={<PendingInvite />} />
      <Route path="/signup/:token" element={<FacilitatedTrialSignupContainer />} />
      <Route element={<Outlet />}>
        <Route path="/signup" element={<SignUpRouteEntry variant={enable1MindLinkSignup() ? 'trial' : 'default'} />} />
        <Route
          path="/signup-agentcontrol"
          element={enableAiConfigOnboarding() ? <SignUpAgentControlRouteEntry /> : <Navigate to="/signup" replace />}
        />
        <Route
          path="/signup-aws"
          element={enableAwsMarketplaceSignup() ? <SignUpAwsRouteEntry /> : <Navigate to="/signup" replace />}
        />
        {/* AWS Marketplace hands buyers off by POSTing to this path, so it must render the
            signup page rather than fall through to the catch-all and bounce to /login. */}
        <Route
          path="/signup-aws/fulfillment"
          element={enableAwsMarketplaceSignup() ? <SignUpAwsRouteEntry /> : <Navigate to="/signup" replace />}
        />
      </Route>
      <Route element={<CreateOrJoinContainer />}>
        {/* disable the /join route and redirect to signup if domain matching is enabled */}
        <Route path="/join" element={isDomainMatchingEnabled ? null : <CreateOrJoinSwitcher />} />
      </Route>

      <Route path="/enroll-mfa" element={<EnrollMFAContainer />} />

      {/* "Forgot password" is the view where the user can enter their email and get a reset link emailed to them */}
      <Route path="/forgot" element={<ForgotPasswordContainer />} />
      {/* "Reset password" is the view where the user can enter in a new password */}
      <Route path="/reset/:passwordResetToken" element={<ResetPasswordContainer />} />
      <Route path="/invite/:invitationToken" element={<AcceptInvitation />} />
      <Route path="/oauth/google" element={<OAuthContainer />} />
      <Route path="/oauth/github" element={<OAuthContainer />} />
      <Route path="/oauth/vercel" element={<OAuthContainer />} />
      <Route path="/team-uses-sso" element={<TeamUsesSsoContainer />} />
      <Route path="/login" element={<LoginContainer />} />
      <Route path="/trial-limit" element={<LazyTrialLimitContainer />} />

      <Route path="/check-your-email" element={<UnauthenticatedEmailVerificationContainer />} />
      <Route
        path="/choose-organization/check-your-email"
        element={<UnauthenticatedEmailVerificationContainer isChooseOrg />}
      />

      {/*
        If we're rendering unauth'd routes for anything else, it's likely that
        the user requested a valid route but isn't logged in. Redirect to the
        login route and pass the requested route along, the login container will
        find this `redirect` param and forward the user along after they log in
        */}
      <Route path="*" element={<NavigateToLogin />} />
    </>
  );

  // We need a static list of routes so that useMatchingPathPattern can inspect them, and a Router context which this
  // wrapping layout route provides
  return (
    <Route element={<UnauthenticatedApp routes={routes} />} hydrateFallbackElement={<LoadingFallback />}>
      {routes}
    </Route>
  );
}

function NavigateToLogin() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  // Preserve full URL (pathname + search + hash) for redirect
  const redirectPath = location.pathname + location.search + location.hash;
  searchParams.set('redirect', redirectPath);
  return <Navigate to={toLogin({ search: searchParams.toString() })} />;
}
